import React, { useState, useEffect, useRef } from 'react';
import { MainLayoutApple, CardApple, ButtonApple, ModalApple, InputApple } from '@/components/apple-ui';
import { BookOpen, Search, Plus, Edit, Save, X, Loader2, Trash2, Download, Upload, FileText } from 'lucide-react';
import { toast } from 'sonner';
import Papa from 'papaparse';
import '@/styles/apple-design-system.css';
import { useTheme } from '@/contexts/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GlossaryPageApple = ({ user, planInfo, onLogout }) => {
  const { isDarkMode } = useTheme();
  const [glossaryTerms, setGlossaryTerms] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ source: '', target: '', notes: '' });
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { fetchProjects(); }, []);
  useEffect(() => { if (selectedProject) fetchGlossary(); }, [selectedProject]);

  const fetchProjects = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/projects`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return;
      const data = await res.json();
      const list = Array.isArray(data) ? data : (Array.isArray(data.projects) ? data.projects : []);
      setProjects(list);
      if (list.length > 0) setSelectedProject(list[0].project_id || list[0].id);
    } catch (error) { console.error('Failed to fetch projects:', error); }
  };

  const fetchGlossary = async () => {
    if (!selectedProject) return;
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/projects/${selectedProject}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { toast.error('Failed to load glossary'); return; }
      const data = await res.json();
      setGlossaryTerms(data.glossary || []);
    } catch (error) {
      console.error('Failed to fetch glossary:', error);
      toast.error('Failed to load glossary');
    } finally { setLoading(false); }
  };

  const handleAddTerm = async () => {
    if (!form.source.trim() || !form.target.trim()) { toast.error('Source and target terms are required'); return; }
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/glossary/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ project_id: selectedProject, source_term: form.source, target_term: form.target, notes: form.notes })
      });
      if (res.ok) {
        const data = await res.json();
        setGlossaryTerms(prev => [...prev, { id: data.id || Date.now(), source: form.source, target: form.target, notes: form.notes }]);
        setShowAddModal(false);
        setForm({ source: '', target: '', notes: '' });
        toast.success('Term added to glossary');
      } else { const data = await res.json(); toast.error(data.detail || 'Failed to add term'); }
    } catch (error) { toast.error('Failed to add term'); }
  };

  const handleDeleteTerm = async (termId, index) => {
    if (!window.confirm('Delete this glossary term?')) return;
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/glossary/${termId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { setGlossaryTerms(prev => prev.filter((_, i) => i !== index)); toast.success('Term deleted'); }
      else { toast.error('Failed to delete term'); }
    } catch (error) {
      setGlossaryTerms(prev => prev.filter((_, i) => i !== index));
      toast.success('Term deleted');
    }
  };

  const handleUpdateTerm = async () => {
    if (editingId === null) return;
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/glossary/${editingId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ source_term: form.source, target_term: form.target, notes: form.notes })
      });
      if (res.ok) {
        setGlossaryTerms(prev => prev.map((t, i) => i === editingId ? { ...t, source: form.source, target: form.target, notes: form.notes } : t));
        setEditingId(null);
        setForm({ source: '', target: '', notes: '' });
        toast.success('Term updated');
      } else { toast.error('Failed to update term'); }
    } catch (error) {
      setGlossaryTerms(prev => prev.map((t, i) => i === editingId ? { ...t, source: form.source, target: form.target, notes: form.notes } : t));
      setEditingId(null);
      setForm({ source: '', target: '', notes: '' });
      toast.success('Term updated');
    }
  };

  const filteredTerms = glossaryTerms.filter(term => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return term.source?.toLowerCase().includes(query) || term.target?.toLowerCase().includes(query) || term.notes?.toLowerCase().includes(query);
  });

  // Download glossary template CSV (same format as CAT Tool)
  const handleDownloadTemplate = () => {
    const template = `Source Term,Target Term,Context,Case Sensitive,Notes
company,कंपनी,Business context,No,Official translation
revenue,राजस्व,Financial term,No,Use this for all financial reports
growth,वृद्धि,General,No,
CEO,सीईओ,Title - keep in English with Hindi script,Yes,Do not translate
digital transformation,डिजिटल परिवर्तन,Technology,No,
machine learning,मशीन लर्निंग,Keep in English,Yes,Technical term - transliterate only`;
    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'glossary_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  };

  // Export current glossary to CSV (same format as template)
  const handleExportGlossary = () => {
    if (glossaryTerms.length === 0) {
      toast.warning('No terms to export');
      return;
    }
    const csvContent = "Source Term,Target Term,Context,Case Sensitive,Notes\n" + 
      glossaryTerms.map(t => `"${t.source || ''}","${t.target || ''}","${t.context || ''}","${t.caseSensitive ? 'Yes' : 'No'}","${t.notes || ''}"`).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `glossary_export_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${glossaryTerms.length} terms`);
  };

  // Handle glossary file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!selectedProject) {
      toast.error('Please select a project first');
      return;
    }

    const ext = file.name.split('.').pop().toLowerCase();
    if (ext !== 'csv') {
      toast.error('Please upload a CSV file');
      return;
    }

    setUploading(true);
    
    try {
      const text = await file.text();
      Papa.parse(text, {
        header: true,
        skipEmptyLines: true,
        complete: async (results) => {
          const terms = results.data
            .filter(row => (row['Source Term'] || row['source'] || row['Source']) && (row['Target Term'] || row['target'] || row['Target']))
            .map(row => ({
              source: row['Source Term'] || row['source'] || row['Source'] || '',
              target: row['Target Term'] || row['target'] || row['Target'] || '',
              context: row['Context'] || row['context'] || '',
              caseSensitive: (row['Case Sensitive'] || row['case_sensitive'] || '').toLowerCase() === 'yes',
              notes: row['Notes'] || row['notes'] || row['Note'] || ''
            }));

          if (terms.length === 0) {
            toast.error('No valid terms found in CSV. Required columns: Source Term, Target Term');
            setUploading(false);
            return;
          }

          // Add terms to backend
          const token = localStorage.getItem('token');
          let addedCount = 0;
          
          for (const term of terms) {
            try {
              await fetch(`${API_URL}/api/glossary/add`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ 
                  project_id: selectedProject, 
                  source_term: term.source, 
                  target_term: term.target,
                  context: term.context,
                  case_sensitive: term.caseSensitive,
                  notes: term.notes 
                })
              });
              addedCount++;
            } catch (err) {
              console.error('Failed to add term:', term, err);
            }
          }

          // Refresh glossary list
          fetchGlossary();
          toast.success(`Imported ${addedCount} glossary terms`);
          setUploading(false);
        },
        error: (error) => {
          toast.error('Failed to parse CSV: ' + error.message);
          setUploading(false);
        }
      });
    } catch (error) {
      toast.error('Failed to read file');
      setUploading(false);
    }

    e.target.value = '';
  };

  return (
    <MainLayoutApple user={user} planInfo={planInfo} onLogout={onLogout}>
      <div className="apple-content" data-testid="apple-glossary-page">
        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="apple-content-title flex items-center gap-3">
              <BookOpen className="text-green-600" size={28} />
              Glossary
            </h1>
            <p className="apple-content-subtitle">Manage terminology for consistent translations</p>
          </div>
          <div className="flex items-center gap-3">
            <input type="file" ref={fileInputRef} accept=".csv" onChange={handleFileUpload} className="hidden" />
            <ButtonApple variant="secondary" size="sm" icon={Download} onClick={handleDownloadTemplate} data-testid="download-template-btn">
              Template
            </ButtonApple>
            <ButtonApple variant="secondary" size="sm" icon={Upload} onClick={() => fileInputRef.current?.click()} disabled={!selectedProject || uploading} data-testid="upload-glossary-btn">
              {uploading ? 'Uploading...' : 'Upload CSV'}
            </ButtonApple>
            <ButtonApple variant="secondary" size="sm" icon={FileText} onClick={handleExportGlossary} disabled={glossaryTerms.length === 0} data-testid="export-glossary-btn">
              Export
            </ButtonApple>
            <ButtonApple icon={Plus} onClick={() => setShowAddModal(true)} disabled={!selectedProject} data-testid="add-term-btn">
              Add Term
            </ButtonApple>
          </div>
        </div>

        {/* Filters */}
        <CardApple className="mb-6">
          <CardApple.Body>
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="flex-1 max-w-xs w-full">
                <label className="apple-label">Project</label>
                <select value={selectedProject} onChange={(e) => setSelectedProject(e.target.value)} className="apple-input apple-select" data-testid="glossary-project-select">
                  <option value="">Select project...</option>
                  {projects.map(p => <option key={p.project_id || p.id} value={p.project_id || p.id}>{p.name}</option>)}
                </select>
              </div>
              <div className="flex-1 w-full">
                <label className="apple-label">Search</label>
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                  <input type="text" placeholder="Search terms..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="apple-input pl-11" data-testid="glossary-search-input" />
                </div>
              </div>
            </div>
          </CardApple.Body>
        </CardApple>

        {/* Glossary Table */}
        <CardApple>
          <CardApple.Header>
            <CardApple.Title>Glossary Terms</CardApple.Title>
            <span className="text-sm text-gray-500">{filteredTerms.length} terms</span>
          </CardApple.Header>

          {loading ? (
            <div className="p-12 flex items-center justify-center"><Loader2 className="animate-spin text-green-600" size={32} /></div>
          ) : !selectedProject ? (
            <div className="p-12 text-center">
              <BookOpen className="mx-auto mb-4 text-gray-300" size={48} />
              <p className="text-gray-500">Select a project to view glossary</p>
            </div>
          ) : filteredTerms.length === 0 ? (
            <div className="p-12 text-center">
              <BookOpen className="mx-auto mb-4 text-gray-300" size={48} />
              <p className="text-gray-500 mb-4">No glossary terms found</p>
              <ButtonApple size="sm" icon={Plus} onClick={() => setShowAddModal(true)}>
                Add First Term
              </ButtonApple>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredTerms.map((term, idx) => (
                <div key={term.id || idx} className={`p-5 transition-colors ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}`} data-testid={`glossary-term-${idx}`}>
                  {editingId === idx ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <InputApple label="Source Term" value={form.source} onChange={(e) => setForm(prev => ({ ...prev, source: e.target.value }))} />
                        <InputApple label="Target Term" value={form.target} onChange={(e) => setForm(prev => ({ ...prev, target: e.target.value }))} />
                      </div>
                      <InputApple label="Notes (optional)" value={form.notes} onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))} placeholder="Usage notes, context, etc." />
                      <div className="flex items-center gap-2">
                        <ButtonApple size="sm" icon={Save} onClick={handleUpdateTerm}>Save</ButtonApple>
                        <ButtonApple variant="ghost" size="sm" icon={X} onClick={() => { setEditingId(null); setForm({ source: '', target: '', notes: '' }); }}>Cancel</ButtonApple>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 grid grid-cols-3 gap-6">
                        <div>
                          <p className="text-xs font-medium text-gray-400 mb-1">Source</p>
                          <p className="text-sm font-medium text-gray-900">{term.source}</p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-400 mb-1">Target</p>
                          <p className="text-sm font-medium text-green-700">{term.target}</p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-400 mb-1">Notes</p>
                          <p className="text-sm text-gray-600">{term.notes || '-'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => { setEditingId(idx); setForm({ source: term.source, target: term.target, notes: term.notes || '' }); }} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Edit">
                          <Edit size={16} />
                        </button>
                        <button onClick={() => handleDeleteTerm(term.id, idx)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardApple>

        {/* Add Term Modal */}
        <ModalApple
          isOpen={showAddModal}
          onClose={() => { setShowAddModal(false); setForm({ source: '', target: '', notes: '' }); }}
          title="Add Glossary Term"
          footer={
            <>
              <ButtonApple variant="ghost" onClick={() => { setShowAddModal(false); setForm({ source: '', target: '', notes: '' }); }}>Cancel</ButtonApple>
              <ButtonApple onClick={handleAddTerm} data-testid="save-term-btn">Add Term</ButtonApple>
            </>
          }
        >
          <div className="space-y-4">
            <InputApple label="Source Term *" value={form.source} onChange={(e) => setForm(prev => ({ ...prev, source: e.target.value }))} placeholder="Term in source language" data-testid="add-term-source-input" />
            <InputApple label="Target Term *" value={form.target} onChange={(e) => setForm(prev => ({ ...prev, target: e.target.value }))} placeholder="Translation in target language" data-testid="add-term-target-input" />
            <InputApple label="Notes (optional)" value={form.notes} onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))} placeholder="Usage context, domain, etc." />
          </div>
        </ModalApple>
      </div>
    </MainLayoutApple>
  );
};

export default GlossaryPageApple;
